import { computed, makeObservable } from 'mobx';
import moment from 'moment';
import React, { useState } from 'react';
import { useAppStateContext } from 'src/AppState/AppState';
import { AutomaticWithdrawalState } from 'src/AppState/AutomaticWithdrawalState';
import {
    TableBody,
    TableCell,
    TableHead,
    TableHeadCell,
    TableHeadRow,
    TableRow,
} from 'src/components/MainTable/MainTable.style';
import { observer } from 'src/utils/mobx-react';
import { HeaderItem, HeaderText, HeaderTitle, HeaderWrapper, Title } from './AutomaticWithdrawalHistory.style';
import { NetDeposit } from 'src/components/FraudAndCompliance/AutomaticWithdrawals/CommonParts/WithdrawalHistory/WithdrawalHistory/NetDeposit';
import { NetDepositState } from 'src/components/FraudAndCompliance/AutomaticWithdrawals/CommonParts/WithdrawalHistory/WithdrawalHistory';
import { AutoWithdrawalHistoryResponseLKType } from 'src/api/config/automaticWithdrawal/getWithdrawalHistoryLK';
import { Amount } from 'src_common/common/amount/Amount';
import { StaffAccountWalletDataType } from 'src/AppState/sdk/staff';
import { AutoMap } from 'src_common/common/mobx-utils/AutoMap';
import { ModelsState } from 'src/AppState/Models/ModelsState';
import { Common, useCommon } from 'src/AppState/Common';

type AutomaticWithdrawalHistoryPropsType = {
    customerId: number;
};

export class AutomaticWithdrawalHistoryState {
    public readonly netDepositState: NetDepositState;

    public constructor(
        private readonly customerId: number,
        private readonly automaticWithdrawalState: AutomaticWithdrawalState,
        private readonly models: ModelsState,
        private readonly common: Common
    ) {
        makeObservable(this);
        this.netDepositState = new NetDepositState(this.customerId, this.common, this.models);
    }

    @computed public get history(): AutoWithdrawalHistoryResponseLKType | null {
        const userId = this.customerId;
        const historyResponse = this.automaticWithdrawalState.history.get(userId).get();
        if (historyResponse.type === 'ready') {
            return historyResponse.value;
        }

        return null;
    }

    @computed public get walletDataLocal(): StaffAccountWalletDataType | null {
        return this.models.getUserModel(this.customerId).walletData.data;
    }
}

export const AutomaticWithdrawalHistory = observer(
    'AutomaticWithdrawalHistory',
    (props: AutomaticWithdrawalHistoryPropsType) => {
        const { automaticWithdrawalState, models } = useAppStateContext();
        const common = useCommon();

        const [stateAutoMap] = useState(
            () =>
                new AutoMap(
                    (customerId: number) =>
                        new AutomaticWithdrawalHistoryState(customerId, automaticWithdrawalState, models, common)
                )
        );

        const state = stateAutoMap.get(props.customerId);
        const history = state.history;

        if (state.walletDataLocal === null || history === null) {
            return null;
        }

        const { currency, createdAt } = state.walletDataLocal;
        const registrationDate = moment(createdAt).format('YYYY-MM-DD');

        const renderTable = (): JSX.Element[] => {
            return history.map((item) => {
                const amount = new Amount(item.amount ?? '0');
                const parsedAmount = amount.div(100);
                return (
                    <div key={item.date}>
                        <TableRow>
                            <TableCell>**** {item.card}</TableCell>
                            <TableCell>{item.ruleType}</TableCell>
                            <TableCell>{item.actionedBy.length === 0 ? '-' : item.actionedBy}</TableCell>
                            <TableCell>{moment(item.date).format('DD/MM/YYYY')}</TableCell>
                            <TableCell>{item.reason?.length === 0 ? '-' : item.reason?.toString()}</TableCell>
                            <TableCell>{parsedAmount.format(currency)}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>{item.note.length === 0 ? '-' : item.note}</TableCell>
                        </TableRow>
                    </div>
                );
            });
        };
        return (
            <div>
                <Title>Automatic Withdrawal History</Title>
                <HeaderWrapper>
                    <HeaderItem>
                        <HeaderTitle>Account Number:</HeaderTitle>
                        <HeaderText>{props.customerId}</HeaderText>
                    </HeaderItem>
                    <HeaderItem>
                        <HeaderTitle>KYC Passed:</HeaderTitle>
                        <HeaderText>N/D</HeaderText>
                    </HeaderItem>
                    <HeaderItem>
                        <HeaderTitle>Registered:</HeaderTitle>
                        <HeaderText>{registrationDate}</HeaderText>
                    </HeaderItem>
                </HeaderWrapper>

                <div>
                    <TableHead>
                        <TableHeadRow>
                            <TableHeadCell>Card</TableHeadCell>
                            <TableHeadCell>Status</TableHeadCell>
                            <TableHeadCell>Actioned By</TableHeadCell>
                            <TableHeadCell>Date</TableHeadCell>
                            <TableHeadCell>Reason</TableHeadCell>
                            <TableHeadCell>Amount</TableHeadCell>
                        </TableHeadRow>
                    </TableHead>
                    <TableBody>{renderTable()}</TableBody>
                </div>

                <NetDeposit
                    currency={currency}
                    netDepositState={state.netDepositState}
                    isCustomerTab={true}
                />
            </div>
        );
    }
);
